<template>
  <div>
    <h5 class="leading-loose">
      CE Information
    </h5>
    <template v-if="isZeroHour">
      <strong>CE credits are not available for this course</strong>
    </template>
    <template v-if="loading">
      <div class=" flex justify-center items-center">
        <div class="animate-spin rounded-full h-10 w-10 mr-4 border-b-2 border-matte-gray" />
        <h6>Loading...</h6>
      </div>
    </template>
    <template v-else-if="showForm || !approved">
      <!--      commenting out in case this logic needs to come back in the future -->
      <!--      <template v-if="error && format ==='LIVE' && brochureDeadlineWindow(brochureDeadline)"> -->
      <!--        <p class="text-sm"> -->
      <!--          CE information, based on profession and state, will be available around <strong>{{ addDaysAndToString(new Date(brochureDeadline), 10) }}</strong> Please check back to ensure this course meets your CE needs. -->
      <!--        </p> -->
      <!--      </template> -->
      <div v-if="error">
        <strong>We're sorry</strong> but we can't find an approval or registration for this course. Please confirm you have the correct state and profession selected. If correct, your selected profession and state may not offer accreditation for this course. Please contact us for clarification at (800) 433-9570 or <a
          href="mailto:customerservice@summit-education.com"
          target="_blank"
        >customerservice@summit-education.com.</a>
      </div>
      <div class="flex flex-row gap-1 md:mb-3 justify-start">
        <StateOptions
          v-model="setState"
          :is-disabled="isZeroHour"
          class="py-2 hidden md:block w-1/3"
          label-key="name"
          variant="ceTracker"
          key-name="id"
          value-key="id"
        />
        <StateOptions
          v-model="setState"
          :is-disabled="isZeroHour"
          class="py-2 w-3/6 md:hidden"
          label-key="name"
          variant="courseMobile"
          key-name="id"
          value-key="id"
        />
        <ProfessionOptions
          v-model="setProfession"
          profession-list-variant="ceValidatorProfessions"
          :is-disabled="isZeroHour"
          class="py-2 hidden md:block w-80"
          label-key="name"
          variant="ceTracker"
        />
        <ProfessionOptions
          v-model="setProfession"
          profession-list-variant="ceValidatorProfessions"
          :is-disabled="isZeroHour"
          class="py-2 w-3/6 md:hidden"
          label-key="name"
          variant="courseMobile"
        />
        <t-button
          class="w-1/6 md:hidden my-2 py-0"
          variant="ctaSecondaryOutlined"
          :disabled="isZeroHour"
          @click="checkCe"
        >
          <t-icon
            class="mr-2 text-sm py-0"
            name="search"
          />
        </t-button>
      </div>
      <t-button
        variant="ctaSecondaryOutlined"
        class="hidden md:block font-bold"
        :disabled="isZeroHour"
        @click="checkCe"
      >
        <t-icon
          class="mr-2 font-bold"
          name="search"
        />
        Check CE
      </t-button>
    </template>
    <template v-else-if="approved">
      <template v-if="certificateText.toUpperCase().includes('NOTICE')">
        <p class="text-xs">
          <strong>NOTICE:</strong>{{ certificateText }}
        </p>
      </template>
      <template v-else-if="certificateText.toUpperCase().includes('SUBMITTED')">
        <p class="font-bold">
          CE Credit PENDING:
        </p>
        <p class="text-sm">
          This course has been submitted and is pending approval for {{ creditHours }} credit hours for {{ profession }} in {{ state }}
        </p>
      </template>
      <template v-else>
        <p class="text-green font-bold leading-loose">
          <t-icon
            class="text-text-secondary mr-2"
            name="thumbs-up"
          />
          CE Credit Available
        </p>
        <p class="text-sm leading-loose">
          This Course offers <b>{{ creditHours }} Credit Hours</b> for {{ profession }} in {{ state }}.
        </p>
      </template>
      <div class="flex items-center pb-4 md:pb-0">
        <button
          class="text-text-secondary text-xs md:text-sm font-bold md:leading-loose"
          @click="showAccreditationStatement()"
        >
          {{ profession === 'SLP' ? 'Registration' : 'Accreditation' }} Statement<span><t-icon
            class="text-text-secondary ml-2"
            name="chevron-down"
          /></span>
        </button>
        <button
          class="text-text-secondary text-xs md:text-sm font-bold md:leading-loose px-3 md:px-5"
          @click="showSearchForm"
        >
          Change State/Profession <span><t-icon
            class="text-text-secondary ml-2"
            name="chevron-down"
          /></span>
        </button>
      </div>
      <a
        v-if="profession === 'SLP'"
        id="asha-text"
        href="https://support.summit-education.com/hc/en-us/articles/213879983-Does-Summit-report-to-CE-broker-ASHA-"
        target="_blank"
        class="text-sm underline decoration-sky-600 hover:decoration-primary-100"
      >Does Summit Report to ASHA?</a>
    </template>
    <template v-if="format === 'PODCAST'">
      <p class="text-xs md:text-sm py-2">
        To receive a certificate of completion for this course you must:<br />
        1) Purchase the course or add it to your Summit Subscription<br />
        2) Log in to your Summit Account to access the evaluation and post test.<br />
        See Satisfactory Completion requirements in the "CE Credit" tab below for details.
      </p>
    </template>
  </div>
</template>

<script>
import useFetchCustom from '../../composables/useFetchCustom';
import StateOptions from '~/components/StateOptions.vue';
import { gtmCheckCe, } from '@/services/gtmManager';
import ProfessionOptions from '~/components/ProfessionOptions.vue';
export default {
  name: 'CeValidator',
  components: {ProfessionOptions, StateOptions },
  props: {
    ceInfo: {
      type: Object,
      required: true,
    },
    session: {
      type: Object,
      required: true,
    },
    ashaReview: {
      type: Boolean,
      default: false,
    },
    userState: {
      type: Object,
      default: () => {},
    },
    userProfession: {
      type: Object,
      default: () => {},
    },
    isZeroHour: {
      type: Boolean,
      default: false,
    },
    brochureDeadline: {
      type: String,
      default: '',
    },
    format: {
      type: String,
      default: '',
    },
  },
  emits: [
    'update', 'show-ce-tab', 'scroll-to-tabs',
  ],
  setup() {
    const { user, loggedIn } = useNuxtApp().$auth;
    return { loggedIn, user };
  },
  data() {
    return {
      loading: false,
      error: false,
      selectedSession: this.session,
      showForm: Object.keys(this.ceInfo).length > 0,
      setState: this.userState,
      setProfession: '', // set in mounted
      certificateText: '',
      creditHours: '',
      profession: this.loggedIn ? { id: this.user.title_code } : () => {},
      state: this.loggedIn ? { id: this.user.state } : () => {},
      isBeforeBrochureDeadline: false,
      deadlineWindow: '',
    };
  },
  computed: {
    approved() {
      return this.ceInfo.ceaId && this.ceInfo.ceaId > 0;
    },
  },
  watch: {
    async session(newSession) {
      this.selectedSession = newSession;
      await this.checkCe();
    },
  },
  async mounted() {
    if (this.loggedIn) {
      await this.checkCe();
    }

    /***** TODO: Remove at some point
      Having to hard set PT/PTA and OT/OTA because we have too many profession.json's and use/set different
      id's (eg: PT vs PT/PTA) at login or course search filter selection so this can be cleaned up if we ever
      set on one profession.json to rule them all
     ****/
    if (this.userProfession.id === 'PT' || this.userProfession.id === 'PTA') {
      this.setProfession = { id: 'PT/PTA' };
    } else if (this.userProfession.id === 'OT' || this.userProfession.id === 'OTA') {
      this.setProfession = { id: 'OT/OTA'};
    } else {
      this.setProfession = this.userProfession.id;
    }
  },
  methods: {
    showAccreditationStatement() {
      // Emit the event
      this.$emit('show-ce-tab');

      // With data
      this.$emit('scroll-to-tabs');
    },

    async checkCe() {
      let newCeInfo = {};

      if (!this.setState.id || !this.setProfession.id) {
        // don't attempt to do a CE check
        return;
      }
      if (!this.isZeroHour) {
        try {
          this.loading = true;
          this.error = false;
          this.showForm = false;
          newCeInfo = await useFetchCustom('GET', '/ce/check', {}, {
            sessionId: this.selectedSession.id,
            state: this.setState.id,
            profession: this.setProfession.id,
            ashaReview: this.ashaReview,
          });
          this.profession = newCeInfo.profession;
          this.state = newCeInfo.state;
          this.creditHours = newCeInfo.creditHours;
          this.certificateText = newCeInfo.certificateText;
        }
        catch (e) {
          this.error = true;
        } finally {
          const data = {
            state: this.setState.id,
            profession: this.setProfession.id,
            approved: !this.error,
            sessionId: this.selectedSession.id,
          };
          gtmCheckCe(data);
          this.loading = false;
          this.showForm = this.error;
          this.$emit('update', newCeInfo);
        }
      }
    },
    showSearchForm() {
      this.showForm = true;
    },
    // if window date is before the current date then show production deadline message
    brochureDeadlineWindow(brochureDeadline) {
      brochureDeadline = this.addDays(new Date(brochureDeadline), 10);
      return brochureDeadline >= new Date();
    },
    addDays(date, days) {
      return new Date(date.setDate(date.getDate() + days));
    },
    addDaysAndToString(date, days) {
      const deadlineDate = new Date(date.setDate(date.getDate() + days));
      return deadlineDate.toString().substring(0, 15);
    },
  },

};
</script>

<style>
.disabled {
  pointer-events: none;
  color: #bfcbd9;
  cursor: not-allowed;
  background-image: none;
  background-color: #eef1f6;
  border-color: #d1dbe5;
}

#asha-text {
  color: blue !important;
}
</style>
